// FinanceHistory — multi-year DCVFD object-level history from Mobius (FY span
// derived from the data, not hardcoded).
// Year-over-year budget vs actual (with value labels + a selectable window) and a
// per-object × fiscal-year actuals pivot with a YoY change indicator on the most
// recent year. Board/staff only (view_finance).

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";
import { useFinanceHistory } from "../hooks/useFinance";
import type { HistoryObjectRow } from "../hooks/useFinance";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import InfoTip from "../components/shared/InfoTip";
import PageIntro from "../components/shared/PageIntro";
import StatusLegend from "../components/shared/StatusLegend";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import {
  asNum,
  usd0,
  usdC,
  TOOLTIP_PROPS,
  ACCENT,
  MUTED,
  GRID,
  topLabel,
  FyRangeToggle,
  useFyWindow,
  type FyWindow,
} from "../components/finance/charts";
import { TH, THR, FetchErrorBanner } from "../components/finance/ui";

/** Tiny ▲/▼ delta chip for year-over-year movement. */
function Delta({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  if (pct === 0)
    return <span className="ml-1 text-[10px] text-theme-muted">→</span>;
  const up = pct > 0;
  return (
    <span
      className={`ml-1 text-[10px] font-semibold ${up ? "text-status-amber" : "text-status-green"}`}
      title="vs prior year"
    >
      {up ? "▲" : "▼"}
      {Math.abs(pct)}%
    </span>
  );
}

export default function FinanceHistory() {
  const { data, isLoading, error } = useFinanceHistory();
  const [fyWin, setFyWin] = useState<FyWindow>(7);

  const allByYear = useMemo(
    () =>
      (data?.by_year ?? []).map((r) => ({
        fy: r.fiscal_year,
        Budget: Math.round(asNum(r.exp_budget)),
        Actual: Math.round(asNum(r.exp_actual)),
      })),
    [data],
  );
  const byYear = useFyWindow(allByYear, fyWin);

  // pivot object_rows → object × fiscal year (actuals), with YoY on latest
  const { years, pivot } = useMemo(() => {
    const rows: HistoryObjectRow[] = data?.object_rows ?? [];
    const yearsSet = new Set<string>();
    const map = new Map<
      string,
      {
        desc: string;
        byFy: Record<string, number>;
        total: number;
        latest: number;
      }
    >();
    for (const r of rows) {
      yearsSet.add(r.fiscal_year);
      const key = r.object_segment;
      const e = map.get(key) ?? {
        desc: r.object_desc ?? "",
        byFy: {},
        total: 0,
        latest: 0,
      };
      const a = asNum(r.actual_amount);
      e.byFy[r.fiscal_year] = a;
      e.total += a;
      map.set(key, e);
    }
    const yrs = [...yearsSet].sort();
    const latest = yrs[yrs.length - 1];
    for (const e of map.values()) e.latest = e.byFy[latest] ?? 0;
    const piv = [...map.entries()]
      .map(([obj, e]) => ({ obj, ...e }))
      .sort((a, b) => b.latest - a.latest);
    return { years: yrs, pivot: piv };
  }, [data]);

  const latestYr = allByYear[allByYear.length - 1];
  const avgActual = allByYear.length
    ? allByYear.reduce((s, y) => s + y.Actual, 0) / allByYear.length
    : 0;
  const prior = allByYear[allByYear.length - 2];
  const yoy =
    latestYr && prior && prior.Actual
      ? Math.round(((latestYr.Actual - prior.Actual) / prior.Actual) * 100)
      : 0;
  const lastTwo = years.slice(-2);
  // FY span of the history, derived from the data (first–last fiscal year) so
  // titles/footnotes widen automatically as new years land — never pinned to
  // FY20–FY26.
  const firstFy = allByYear[0]?.fy ?? "";
  const lastFy = allByYear[allByYear.length - 1]?.fy ?? "";
  const fyRangeLabel = firstFy && lastFy ? `${firstFy}–${lastFy}` : "";

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title={`Multi-Year History${fyRangeLabel ? ` (${fyRangeLabel})` : ""}`}
          subtitle="DCVFD object-level budget vs. actual by fiscal year — authoritative from Mobius (Dept 656500, Dale City)."
        />
        <PageIntro>
          How our spending has trended over the past several years — what we
          budgeted versus what we actually spent, and which categories are
          rising or falling.
        </PageIntro>
      </div>

      <FetchErrorBanner error={error} what="finance history" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label={`${latestYr?.fy ?? "Latest"} Actual`}
          value={usdC(latestYr?.Actual ?? 0)}
          subtitle="Expenditure spend"
          accent="neutral"
          isLoading={isLoading}
        />
        <KPICard
          label={`${allByYear.length}-Year Avg Actual`}
          value={usdC(avgActual)}
          subtitle={`${allByYear.length} fiscal years`}
          accent="neutral"
          isLoading={isLoading}
        />
        <KPICard
          label="YoY Change"
          value={`${yoy >= 0 ? "+" : ""}${yoy}%`}
          subtitle={`${latestYr?.fy} vs ${prior?.fy}`}
          accent={yoy > 10 ? "amber" : "neutral"}
          isLoading={isLoading}
        />
        <KPICard
          label={`${latestYr?.fy} Budget`}
          value={usdC(latestYr?.Budget ?? 0)}
          subtitle="Expenditure appropriation"
          accent="neutral"
          isLoading={isLoading}
        />
      </div>

      <ChartCard
        title="Budget vs Actual by Fiscal Year"
        subtitle="Expenditure side"
        empty={!isLoading && byYear.length === 0}
        headerRight={<FyRangeToggle value={fyWin} onChange={setFyWin} />}
      >
        {byYear.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={byYear}
              margin={{ top: 24, right: 16, bottom: 4, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="fy" tick={{ fontSize: 12, fill: MUTED }} />
              <YAxis
                tickFormatter={(v) => usdC(Number(v))}
                tick={{ fontSize: 12, fill: MUTED }}
                width={56}
              />
              <Tooltip
                formatter={(v, k) => [usd0(Number(v)), k as string]}
                {...TOOLTIP_PROPS}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="Budget"
                fill={MUTED}
                fillOpacity={0.55}
                radius={[3, 3, 0, 0]}
                maxBarSize={48}
              />
              <Bar
                dataKey="Actual"
                fill={ACCENT}
                radius={[3, 3, 0, 0]}
                maxBarSize={48}
              >
                <LabelList dataKey="Actual" position="top" content={topLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Actual Spend by Object × Fiscal Year"
        subtitle={`Each object's actual expenditure per year · ▲▼ = ${lastTwo[1] ?? ""} vs ${lastTwo[0] ?? ""}`}
        empty={!isLoading && pivot.length === 0}
        minHeight={120}
        headerRight={
          <StatusLegend
            items={[
              { swatch: "▲", label: "Spending up vs prior year" },
              { swatch: "▼", label: "Spending down vs prior year" },
            ]}
          />
        }
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[820px] text-left">
            <caption className="sr-only">
              Actual spend by object and fiscal year
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  <span className="inline-flex items-center gap-1">
                    Object <InfoTip term="object" />
                  </span>
                </th>
                <th scope="col" className={TH}>
                  Description
                </th>
                {years.map((y) => (
                  <th scope="col" key={y} className={THR}>
                    {y}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 10 }).map((_, i) => (
                    <tr
                      key={i}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      {Array.from({ length: years.length + 2 }).map((__, j) => (
                        <td key={j} className="px-3 py-2">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width: `${40 + ((i * 7 + j * 11) % 50)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : pivot.map((row) => {
                    const a = row.byFy[lastTwo[0]] ?? null;
                    const b = row.byFy[lastTwo[1]] ?? null;
                    const delta =
                      a && b ? Math.round(((b - a) / a) * 100) : null;
                    return (
                      <tr
                        key={row.obj}
                        className="hover:bg-theme-overlay/50 transition-colors"
                      >
                        <td className="px-3 py-2 text-sm font-mono text-theme-primary">
                          {row.obj}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-theme-secondary max-w-[220px] truncate"
                          title={row.desc}
                        >
                          {row.desc}
                        </td>
                        {years.map((y, yi) => (
                          <td
                            key={y}
                            className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary whitespace-nowrap"
                          >
                            {row.byFy[y] ? (
                              usdC(row.byFy[y])
                            ) : (
                              <span className="text-theme-muted/50">—</span>
                            )}
                            {yi === years.length - 1 && <Delta pct={delta} />}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <p className="text-xs text-theme-muted">
        Source: Mobius OTBI "Budgetary Control – Balances Real Time" (Dept
        656500), periods summed per fiscal year. Expenditure objects only. Each
        fiscal year runs Jul–Jun and is labeled by the year it ends (e.g. FY26 =
        Jul 2025–Jun 2026)
        {fyRangeLabel ? `; data spans ${fyRangeLabel}.` : "."}
      </p>
    </PageShell>
  );
}
